import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import {
  experienceError,
  safeExperienceDestination,
} from '@/lib/member-experience';
import { popupsService as service } from '@/services/popups.service';
import { PopupDialog } from './PopupDialog';

export function MemberPopup() {
  const { user, isAuthenticated } = useAuthStore();
  if (!isAuthenticated || !user || user.role !== 'STUDENT') return null;
  return <StudentPopup key={user.id} userId={user.id} />;
}

function StudentPopup({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const [finished, setFinished] = useState(false);
  const [ready, setReady] = useState(false);
  const [shownPopupId, setShownPopupId] = useState<string | null>(null);
  const popup = useQuery({
    queryKey: ['member-popup', userId],
    queryFn: service.active,
    enabled: !finished,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: 'always',
    refetchOnMount: 'always',
  });
  const view = useMutation({ mutationFn: service.view });
  const popupId = popup.data?.id;
  useEffect(() => {
    if (!popupId || finished) return;
    if (shownPopupId && popupId !== shownPopupId) {
      setFinished(true);
      return;
    }
    let timer: ReturnType<typeof setTimeout>;
    let blocked: boolean | undefined;
    const schedule = () => {
      const otherDialog = [
        ...document.querySelectorAll('[role="dialog"], [role="alertdialog"]'),
      ].some((dialog) => !dialog.hasAttribute('data-member-popup'));
      if (blocked === otherDialog) return;
      blocked = otherDialog;
      clearTimeout(timer);
      if (otherDialog) setReady(false);
      else
        timer = setTimeout(() => {
          setShownPopupId(popupId);
          setReady(true);
        }, 1500);
    };
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, { childList: true, subtree: true });
    schedule();
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [popupId, finished, shownPopupId]);

  if (
    finished ||
    !popup.data ||
    popup.isError ||
    (shownPopupId && popupId !== shownPopupId)
  )
    return null;
  const content = popup.data;
  function finish(followLink = false) {
    if (view.isPending) return;
    view.mutate(content.id, {
      onSuccess: () => {
        setFinished(true);
        const destination =
          followLink && safeExperienceDestination(content.ctaUrl || undefined);
        if (destination) {
          if (destination.startsWith('/')) navigate(destination);
          else window.location.assign(destination);
        }
      },
      onError: (error) => {
        const status = (error as { response?: { status?: number } }).response
          ?.status;
        if (status === 403 || status === 404) setFinished(true);
      },
    });
  }
  return (
    <PopupDialog
      open={ready}
      popup={content}
      pending={view.isPending}
      onClose={() => finish()}
      onDismiss={() => setFinished(true)}
      onAction={() => finish(true)}
      error={
        view.isError
          ? experienceError(
              view.error,
              'Não foi possível registrar a leitura. Tente novamente.',
            )
          : undefined
      }
    />
  );
}
